package nl.volleyplanner.nevoboservice.api

import io.ktor.serialization.kotlinx.*
import io.ktor.util.reflect.*
import kotlin.time.Instant
import kotlinx.datetime.LocalDateTime
import kotlinx.serialization.*
import kotlinx.serialization.modules.SerializersModule
import kotlinx.serialization.modules.polymorphic
import kotlinx.serialization.modules.subclass
import kotlin.time.ExperimentalTime

@Serializable
data class PagedResponse<T>(
    @SerialName("@type")
    val type: String,
    @SerialName("hydra:totalItems")
    val totalItems: Int,
    @SerialName("hydra:member")
    val records: List<T>,
    @SerialName("hydra:view")
    val paginationData: PaginationData
)

@Serializable
data class PaginationData(
    @SerialName("@id")
    val currentPageUrl: String,
    @SerialName("hydra:first")
    val firstPageUrl: String?,
    @SerialName("hydra:last")
    val lastPageUrl: String?,
    @SerialName("hydra:next")
    val nextPageUrl: String?
) {
    companion object {
        private val pageRegex = Regex("""^.*page=(\d+)$""")
    }

    val currentPage = when {
        lastPageUrl == null -> 1
        else -> pageRegex.find(currentPageUrl)!!.groupValues[1].toInt()
    }

    val lastPage = when {
        lastPageUrl != null -> pageRegex.find(lastPageUrl)!!.groupValues[1].toInt()
        else -> currentPage
    }
}

val identifiersModule = SerializersModule {
    contextual(WedstrijdUUIDIdentifier::class, WedstrijdUUIDIdentifierSerializer())
    polymorphic(NevoboAPIIdentifier::class) {
        subclass(WedstrijdIdentifier::class, WedstrijdIdentifierSerializer())
        subclass(WedstrijdUUIDIdentifier::class, WedstrijdUUIDIdentifierSerializer())
    }
}

interface NevoboAPIType {
    fun identifier() : NevoboAPIIdentifier<*>
}

// TODO: Revamp typeinfo, creating identifierURLpath etc.
abstract class NevoboAPIIdentifier<T : NevoboAPIType>(
    @Transient
    private val typeInfo: TypeInfo = typeInfo<NevoboAPIIdentifier<T>>()
) {
    companion object {
        @Suppress("UNCHECKED_CAST")
        @OptIn(InternalSerializationApi::class, ExperimentalSerializationApi::class)
        inline fun <reified  T : NevoboAPIIdentifier<*> > getIdentifierFromURLPath(pathString: String) : T {
            val serializer = identifiersModule.serializerForTypeInfo(typeInfo<T>()) as NevoboIdentifierSerializer<NevoboAPIIdentifier<*>>
            return serializer.deserialize(pathString) as T
        }
    }

    @Suppress("UNCHECKED_CAST")
    @OptIn(InternalSerializationApi::class, ExperimentalSerializationApi::class)
    fun getIdentifierURLPath() : String {
        val serializer = (identifiersModule.serializerForTypeInfo(typeInfo) as NevoboIdentifierSerializer<NevoboAPIIdentifier<T>>)
        return serializer.getPathString(this)
    }
}

class SeizoenIdentifierSerializer : NevoboIdentifierSerializer<SeizoenIdentifier>(
    "/seizoenen/{seizoenNaam}",
    { pathVariables -> SeizoenIdentifier(pathVariables.getValue("seizoenNaam")) },
    { instance -> mapOf("seizoenNaam" to instance.naam) }
)

@Serializable(with = SeizoenIdentifierSerializer::class)
data class SeizoenIdentifier(
    val naam: String
) : NevoboAPIIdentifier<Seizoen>(typeInfo) {
    companion object {
        private val typeInfo = typeInfo<SeizoenIdentifier>()
    }
}

@OptIn(ExperimentalTime::class)
@Serializable
data class Seizoen(
    @SerialName("@id")
    val seizoenIdentifier: SeizoenIdentifier,
    @SerialName("beginjaar")
    val beginJaar: Int,
    @SerialName("eindjaar")
    val eindJaar: Int,
    /**
     * Value depends on the timestamp of the request:
     * vorig: For past seasons (assume when einddatum <= now)
     * huidig: For current season (assume when begindatum <= now <= einddatum)
     * volgend: For upcoming seasons (assume when now <= begindatum)
     */
    val type: String,
    val afkorting: String,
    @SerialName("begindatum")
    @Contextual
    val beginDatum: Instant,
    @SerialName("einddatum")
    @Contextual
    val eindDatum: Instant,
    /**
     * Not implemented:
     *   beachjaar, begindatumBeachseizoen, einddatumBeachseizoen
     *   "teamdispensatieverzoekenGeopend": boolean,
     *   "teamdispensatieverzoekenOpen": boolean,
     *   "officialaanmeldingGeopend": boolean,
     *   "officialaanmeldingOpen": boolean
     */
) : NevoboAPIType {
    override fun identifier() = seizoenIdentifier
}

class VerenigingIdentifierSerializer : NevoboIdentifierSerializer<VerenigingIdentifier>(
    "/relatiebeheer/verenigingen/{verenigingCode}",
    { pathVariables -> VerenigingIdentifier(pathVariables.getValue("verenigingCode")) },
    { instance -> mapOf("verenigingCode" to instance.verenigingCode) }
)

@Serializable(with = VerenigingIdentifierSerializer::class)
data class VerenigingIdentifier(
    val verenigingCode: String
) : NevoboAPIIdentifier<Vereniging>(typeInfo) {
    companion object {
        private val typeInfo = typeInfo<VerenigingIdentifier>()
    }
}

@OptIn(ExperimentalTime::class)
@Serializable
data class Vereniging(
    @SerialName("@id")
    val verenigingIdentifier: VerenigingIdentifier,
    val regio: String,
    @SerialName("organisatiecode")
    val organisatieCode: String,
    val naam: String,
    @SerialName("vestigingsplaats")
    val vestigingsPlaats: String?,
    @Contextual
    val oprichting: Instant?,
    val website: String?,
    val email: String?,
    val telefoon: String?,
    val gemeente: String?,
    val provincie: String?
) : NevoboAPIType {
    override fun identifier() = verenigingIdentifier
}

class NiveauIdentifierSerializer : NevoboIdentifierSerializer<NiveauIdentifier>(
    pathTemplate = "/competitie/niveaus/{niveauId}",
    deserialize = { pathVariables -> NiveauIdentifier(pathVariables.getValue("niveauId")) },
    serialize = { instance -> mapOf("niveauId" to instance.niveauId) }
)

@Serializable(with = NiveauIdentifierSerializer::class)
data class NiveauIdentifier(
    val niveauId: String
) : NevoboAPIIdentifier<Niveau>(typeInfo) {
    companion object {
        private val typeInfo = typeInfo<NiveauIdentifier>()
    }
}

@Serializable
data class Niveau(
    @SerialName("@id")
    val niveauIdentifier: NiveauIdentifier,
    val omschrijving: String,
    val omschrijvingKort: String
) : NevoboAPIType {
    override fun identifier() = niveauIdentifier

}

class KlasseIdentifierSerializer : NevoboIdentifierSerializer<KlasseIdentifier>(
    "/competitie/klasses/{regio}/{competitieId}/{klasseId}",
    { pathVariables -> KlasseIdentifier(pathVariables.getValue("regio"), pathVariables.getValue("competitieId"), pathVariables.getValue("klasseId")) },
    { instance -> mapOf("regio" to instance.regio, "competitieId" to instance.competitieId, "klasseId" to instance.klasseId) }
)

@Serializable(with = KlasseIdentifierSerializer::class)
data class KlasseIdentifier(
    val regio: String,
    val competitieId: String,
    val klasseId: String
) : NevoboAPIIdentifier<Klasse>(typeInfo) {
    companion object {
        private val typeInfo = typeInfo<KlasseIdentifier>()
    }
}

@Serializable
data class Klasse(
    @SerialName("@id")
    val klasseIdentifier: KlasseIdentifier,
    /**
     * Name of klasse
     */
    val omschrijving: String,
    val niveau: NiveauIdentifier,
    val doordraaien: Boolean,
    val wisselsysteem: String
) : NevoboAPIType {
    override fun identifier() = klasseIdentifier
}

class CompetitieTypeIdentifierSerializer : NevoboIdentifierSerializer<CompetitieTypeIdentifier>(
    "/competitie/competitietypes/{competitieTypeId}",
    { pathVariables -> CompetitieTypeIdentifier(pathVariables.getValue("competitieTypeId")) },
    { instance -> mapOf("competitieTypeId" to instance.competitieTypeId) }
)

@Serializable(with = CompetitieTypeIdentifierSerializer::class)
data class CompetitieTypeIdentifier(
    /**
     * Values 2024-2025
     * - competitie
     * - bekertoernooi
     * - kampioenschappen
     * - eerste-helft
     * - tweede-helft
     * - zitvolleybal
     * - recreanten
     */
    val competitieTypeId: String
) : NevoboAPIIdentifier<CompetitieType>(typeInfo) {
    companion object {
        val typeInfo = typeInfo<CompetitieTypeIdentifier>()
    }
}

@Serializable
data class CompetitieType(
    @SerialName("@id")
    val competitieTypeIdentifier: CompetitieTypeIdentifier,
    val omschrijving: String,
    val afkorting: String,
    @SerialName("isBeachcompetitietype")
    val isBeachCompetitieType: Boolean
) : NevoboAPIType {
    override fun identifier() = competitieTypeIdentifier
}

class CompetitieIdentifierSerializer : NevoboIdentifierSerializer<CompetitieIdentifier>(
    "/competitie/competities/{regio}/{competitieId}",
    { pathVariables -> CompetitieIdentifier(pathVariables.getValue("regio"), pathVariables.getValue("competitieId")) },
    { instance -> mapOf("regio" to instance.regio, "competitieId" to instance.competitieId) }
)

@Serializable(with = CompetitieIdentifierSerializer::class)
data class CompetitieIdentifier(
    val regio: String,
    val competitieId: String
) : NevoboAPIIdentifier<Competitie>(typeInfo) {
    companion object {
        private val typeInfo = typeInfo<CompetitieIdentifier>()
    }
}

@Serializable
data class Competitie(
    @SerialName("@id")
    val competitieIdentifier: CompetitieIdentifier,
    val regio: String,
    @SerialName("competitietype")
    val competitieType: CompetitieTypeIdentifier,
    val seizoen: SeizoenIdentifier,
    @SerialName("correspondentiegroep")
    val correspondentieGroep: String,
    val omschrijving: String,
    @SerialName("dwf")
    val dwfRequirement: String,
    @SerialName("reglementaireUitslagMogelijk")
    val reglementaireUitslagMogelijk: Boolean
) : NevoboAPIType {
    override fun identifier() = competitieIdentifier
}

class FaseIdentifierSerializer : NevoboIdentifierSerializer<FaseIdentifier>(
    "/competitie/fases/{regio}/{competitieId}/{faseId}",
    { pathVariables -> FaseIdentifier(pathVariables.getValue("regio"), pathVariables.getValue("competitieId"), pathVariables.getValue("faseId")) },
    { instance -> mapOf("regio" to instance.regio, "competitieId" to instance.competitieId, "faseId" to instance.faseId) }
)

@Serializable(with = FaseIdentifierSerializer::class)
data class FaseIdentifier(
    val regio: String,
    val competitieId: String,
    val faseId: String
) : NevoboAPIIdentifier<Fase>(typeInfo) {
    companion object {
        private val typeInfo = typeInfo<FaseIdentifier>()
    }
}

@Serializable
data class Fase(
    @SerialName("@id")
    val faseIdentifier: FaseIdentifier,
    val omschrijving: String,
    val isNaCompetitie: Boolean,
    /**
     * Not yet applicable:
     * isWijzigenMogelijk
     * datumPublicatiePouleIndeling
     * datumPublicatieWedstrijdschema
     * datumWedstrijdSchemaDefinitief
     * uiterlijkeDatumOnderlingeWedstrijd
     * uiterlijkeDatumUitstellen
     * minimumaantalDagenVoorOudeWedstrijddatum
     * minimumaantalDagenVoorNieuweWedstrijddatum
     */
) : NevoboAPIType {
    override fun identifier() = faseIdentifier
}

class PouleIdentifierSerializer : NevoboIdentifierSerializer<PouleIdentifier>(
    "/competitie/poules/{regio}/{competitieId}/{pouleId}",
    { pathVariables -> PouleIdentifier(pathVariables.getValue("regio"), pathVariables.getValue("competitieId"), pathVariables.getValue("pouleId")) },
    { instance -> mapOf("regio" to instance.regio, "competitieId" to instance.competitieId, "pouleId" to instance.pouleId) }
)

@Serializable(with = PouleIdentifierSerializer::class)
data class PouleIdentifier(
    val regio: String,
    val competitieId: String,
    val pouleId: String
) : NevoboAPIIdentifier<Poule>(typeInfo) {
    companion object {
        private val typeInfo = typeInfo<PouleIdentifier>()
    }

    fun getCompetitieIdentifier() = CompetitieIdentifier(regio, competitieId)
}

@OptIn(ExperimentalTime::class)
@Serializable
data class Poule(
    @SerialName("@id")
    val pouleIdentifier: PouleIdentifier,
    val klasse: KlasseIdentifier,
    val fase: FaseIdentifier,
    val omschrijving: String,
    val afkorting: String,
    @Contextual
    val laatsteUpdate: Instant?,
    val puntentelmethode: PuntenTelMethodeIdentifier
) : NevoboAPIType {
    override fun identifier() = pouleIdentifier
}

class PuntenTelMethodeIdentifierSerializer : NevoboIdentifierSerializer<PuntenTelMethodeIdentifier>(
    "/competitie/puntentelmethodes/{puntenTelMethodId}",
    { pathVariables -> PuntenTelMethodeIdentifier(pathVariables.getValue("puntenTelMethodId")) },
    { instance -> mapOf("puntenTelMethodId" to instance.puntenTelMethodId) }
)

@Serializable(with = PuntenTelMethodeIdentifierSerializer::class)
data class PuntenTelMethodeIdentifier(
    val puntenTelMethodId: String
) : NevoboAPIIdentifier<PuntenTelMethode>(typeInfo) {
    companion object {
        private val typeInfo = typeInfo<PuntenTelMethodeIdentifier>()
    }
}

@Serializable
data class PuntenTelMethodeMogelijkeUitslag(
    val setsA: Double,
    val setsB: Double,
    val puntenA: Int,
    val puntenB: Int
)

@Serializable
data class PuntenTelMethode(
    @SerialName("@id")
    val puntenTelMethodeIdentifier: PuntenTelMethodeIdentifier,
    val mogelijkeUitslagen: List<PuntenTelMethodeMogelijkeUitslag>,
    val omschrijving: String,
    val afkorting: String,
    val minimumPuntenReguliereSet: Int,
    val minimumPuntenBeslissendeSet: Int,
    val minimumVerschilReguliereSet: Int,
    val minimumVerschilBeslissendeSet: Int,
    val heeftVerdubbeldeWeergave: String,
) : NevoboAPIType {
    override fun identifier() = puntenTelMethodeIdentifier
}

class TeamIdentifierSerializer : NevoboIdentifierSerializer<TeamIdentifier>(
    "/competitie/teams/{verenigingCode}/{teamType}/{teamNummer}",
    { pathVariables -> TeamIdentifier(pathVariables.getValue("verenigingCode"), pathVariables.getValue("teamType"), pathVariables.getValue("teamNummer")) },
    { instance -> mapOf("verenigingCode" to instance.verenigingCode, "teamType" to instance.teamType, "teamNummer" to instance.teamNummer) }
)

@Serializable(with = TeamIdentifierSerializer::class)
data class TeamIdentifier(
    val verenigingCode: String,
    val teamType: String,
    val teamNummer: String
) : NevoboAPIIdentifier<Team>(typeInfo) {
    companion object {
        private val typeInfo = typeInfo<TeamIdentifier>()
    }
}

@Serializable
data class Team(
    @SerialName("@id")
    val teamIdentifier: TeamIdentifier,
    val uuid: String,
    val vereniging: VerenigingIdentifier,
    val volgnummer: String,
    val naam: String
) : NevoboAPIType {
    override fun identifier() = teamIdentifier
}

class PouleIndelingIdentifierSerializer : NevoboIdentifierSerializer<PouleIndelingIdentifier>(
    "/competitie/pouleindelingen/{regio}/{competitieId}/{pouleId}/{pouleIndelingId}",
    { pathVariables -> PouleIndelingIdentifier(pathVariables.getValue("regio"), pathVariables.getValue("competitieId"), pathVariables.getValue("pouleId"), pathVariables.getValue("pouleIndelingId")) },
    { instance -> mapOf("regio" to instance.regio, "competitieId" to instance.competitieId, "pouleId" to instance.pouleId, "pouleIndelingId" to instance.pouleIndelingId) }
)

@Serializable(with = PouleIndelingIdentifierSerializer::class)
data class PouleIndelingIdentifier(
    val regio: String,
    val competitieId: String,
    val pouleId: String,
    val pouleIndelingId: String
) : NevoboAPIIdentifier<PouleIndeling>(typeInfo) {
    companion object {
        private val typeInfo = typeInfo<PouleIndelingIdentifier>()
    }

    fun getCompetitieIdentifier() = CompetitieIdentifier(regio, competitieId)
}

@Serializable
data class PouleIndeling(
    @SerialName("@id")
    val pouleIndelingIdentifier: PouleIndelingIdentifier,
    val poule: PouleIdentifier,
    val team: TeamIdentifier,
    @SerialName("indelingsletter")
    val indelingsLetter: String,
    val omschrijving: String,
    val positie: Int?,
    val gespeeld: Int?,
    val punten: Int?,
    val wedstrijdenWinst: Int?,
    val wedstrijdenVerlies: Int?,
    val setsVoor: Int?,
    val setsTegen: Int?,
    val puntenVoor: Int?,
    val puntenTegen: Int?,
    val kampioen: Boolean?,
    /**
     * {
     *  waarde: "promotie", "degradatie", "degradatiewedstrijden", "handhaving"
     *  omschrijving: "<afhankelijk van waarde>"
     * }
     */
    val status: WedstrijdStatus?

) : NevoboAPIType {
    override fun identifier() = pouleIndelingIdentifier
}

class SpeelveldIdentifierSerializer : NevoboIdentifierSerializer<SpeelveldIdentifier>(
    "/accommodatie/speelvelden/{sporthalId}/{speelzaalId}/{speelveldId}",
    { pathVariables -> SpeelveldIdentifier(pathVariables.getValue("sporthalId"), pathVariables.getValue("speelzaalId"), pathVariables.getValue("speelveldId")) },
    { instance -> mapOf("sporthalId" to instance.sporthalId, "speelzaalId" to instance.speelzaalId, "speelveldId" to instance.speelveldId) }
)

// speelveld /accommodatie/speelvelden/kees-boekehal-bilthoven/kees-boekehal/veld-3
// speelzaal /accommodatie/speelzalen/kees-boekehal-bilthoven/kees-boekehal
// sporthal /accommodatie/sporthallen/kees-boekehal-bilthoven
@Serializable(with = SpeelveldIdentifierSerializer::class)
data class SpeelveldIdentifier(
    val sporthalId: String,
    val speelzaalId: String,
    val speelveldId: String
) : NevoboAPIIdentifier<Speelveld>(typeInfo) {
    companion object {
        private val typeInfo = typeInfo<SpeelveldIdentifier>()
    }
}

@Serializable
data class Speelveld(
    @SerialName("@id")
    val speelveldIdentifier: SpeelveldIdentifier,
    val speelzaal: SpeelzaalIdentifier,
    @SerialName("type_centercourt")
    val typeCentercourt: Boolean,
    @SerialName("type_halfcourt")
    val typeHalfCourt: Boolean,
    @SerialName("type_normaal")
    val typeNormaal: Boolean,
    @SerialName("type_cmv")
    val typeCMV: Boolean,
    val aanduiding: String
    // Not yet applicable:
    // type_badminton
) : NevoboAPIType {
    override fun identifier() = speelveldIdentifier
}

class SpeelzaalIdentifierSerializer : NevoboIdentifierSerializer<SpeelzaalIdentifier>(
    "/accommodatie/speelzalen/{sporthalId}/{speelzaalId}",
    { pathVariables -> SpeelzaalIdentifier(pathVariables.getValue("sporthalId"), pathVariables.getValue("speelzaalId")) },
    { instance -> mapOf("sporthalId" to instance.sporthalId, "speelzaalId" to instance.speelzaalId) }
)

@Serializable(with = SpeelzaalIdentifierSerializer::class)
data class SpeelzaalIdentifier(
    val sporthalId: String,
    val speelzaalId: String
) : NevoboAPIIdentifier<Speelzaal>(typeInfo) {
    companion object {
        private val typeInfo = typeInfo<SpeelzaalIdentifier>()
    }
}

@Serializable
data class Speelzaal(
    @SerialName("@id")
    val speelzaalIdentifier: SpeelzaalIdentifier,
    val sporthal: SporthalIdentifier,
    val omschrijving: String,
    val plaats: String
) : NevoboAPIType {
    override fun identifier() = speelzaalIdentifier
}

class SporthalIdentifierSerializer : NevoboIdentifierSerializer<SporthalIdentifier>(
    "/accommodatie/sporthallen/{sporthalId}",
    { pathVariables -> SporthalIdentifier(pathVariables.getValue("sporthalId")) },
    { instance -> mapOf("sporthalId" to instance.sporthalId) }
)

@Serializable(with = SporthalIdentifierSerializer::class)
data class SporthalIdentifier(
    val sporthalId: String
) : NevoboAPIIdentifier<Sporthal>(typeInfo) {
    companion object {
        private val typeInfo = typeInfo<SporthalIdentifier>()
    }
}

@Serializable
data class Sporthal(
    @SerialName("@id")
    val sporthalIdentifier: SporthalIdentifier,
    val naam: String,
    val telefoon: String,
    val plaats: String
    // Not yet applicable:
    // Adres: {} object
) : NevoboAPIType {
    override fun identifier() = sporthalIdentifier
}

class WedstrijdIdentifierSerializer : NevoboIdentifierSerializer<WedstrijdIdentifier>(
    "/competitie/wedstrijden/{regio}/{competitieId}/{pouleId}/{wedstrijdCode}/{volgnummer}/{instantie}",
    { pathVariables -> WedstrijdIdentifier(
        pathVariables.getValue("regio"), pathVariables.getValue("competitieId"), pathVariables.getValue("pouleId"),
        pathVariables.getValue("wedstrijdCode"), pathVariables.getValue("volgnummer"), pathVariables.getValue("instantie")) },
    { instance -> mapOf("regio" to instance.regio, "competitieId" to instance.competitieId, "pouleId" to instance.pouleId,
        "wedstrijdCode" to instance.wedstrijdCode, "volgnummer" to instance.volgnummer, "instantie" to instance.instantie) }
)

@Serializable(with = WedstrijdIdentifierSerializer::class)
data class WedstrijdIdentifier(
    val regio: String,
    val competitieId: String,
    val pouleId: String,
    val wedstrijdCode: String,
    val volgnummer: String,
    val instantie: String
) : NevoboAPIIdentifier<Wedstrijd>(typeInfo) {
    companion object {
        private val typeInfo = typeInfo<WedstrijdIdentifier>()
    }

    fun getCompetitieIdentifier() = CompetitieIdentifier(regio, competitieId)
    fun getPouleIdentifier() = PouleIdentifier(regio, competitieId, pouleId)
}

class WedstrijdUUIDIdentifierSerializer : NevoboIdentifierSerializer<WedstrijdUUIDIdentifier>(
    "/competitie/wedstrijden/{uuid}",
    { pathVariables -> WedstrijdUUIDIdentifier(pathVariables.getValue("uuid")) },
    { instance -> mapOf("uuid" to instance.uuid) }
)

@Serializable(with = WedstrijdUUIDIdentifierSerializer::class)
data class WedstrijdUUIDIdentifier(
    val uuid: String
) : NevoboAPIIdentifier<Wedstrijd>(typeInfo) {
    companion object {
        private val typeInfo = typeInfo<WedstrijdUUIDIdentifier>()
    }
}

// TODO: Rename to Status, it is re-used for other purposes too
@Serializable
data class WedstrijdStatus(
    val waarde: String,     // intern, concept, gepland, uitgesteld, afgebroken, gespeeld, verwijderd, overspelen, vervallen
    val omschrijving: String
)

@Serializable
data class SetStand(
    val set: Int,
    val puntenA: Int,
    val puntenB: Int
)

/**
 * Identifier: /competitie/wedstrijden/<regio>/<competitie_id>/<poule_id>/<wedstrijd_code>/<volgnummer>/<instantie>
 */
@OptIn(ExperimentalTime::class)
@Serializable
data class Wedstrijd(
    @SerialName("@id")
    val wedstrijdIdentifier: WedstrijdIdentifier,
    val uuid: String,
    @SerialName("wedstrijdleiding")
    val wedstrijdLeiding: VerenigingIdentifier,
    @SerialName("code")
    val wedstrijdCode: String,
    @SerialName("lengte")
    val lengteInMinuten: Int,
    @SerialName("setstanden")
    val setStanden: List<SetStand>?,
    val urlDwf: String?,
    val poule: PouleIdentifier,
    val teams: List<PouleIndelingIdentifier>,
    @SerialName("datum")
    @Contextual
    val datum: Instant?,
    @SerialName("tijdstip")
    @Contextual
    val datumEnTijdstip: Instant?,
    val speelveld: SpeelveldIdentifier?,
    val speelzaal: SpeelzaalIdentifier?,
    val sporthal: SporthalIdentifier?,
    val status: WedstrijdStatus
) : NevoboAPIType {
    override fun identifier() = wedstrijdIdentifier
}
